import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getPreviousPeriodDates, getPreviousYearDates } from "../utils/periods";
import { BookingPaceItem } from "../models/responses";
import { getPlatformName } from "../utils/random";

export type BookingPaceStats = {
    grossIncome: number;
    netIncome: number;
    futureGrossIncome: number;
    futureNetIncome: number;
    currentGrossIncome: number;
    currentNetIncome: number;
    grossAdr: number;
    netAdr: number;
    futureGrossAdr: number;
    futureNetAdr: number;
    currentGrossAdr: number;
    currentNetAdr: number;
    daysBooked: number;
    currentDaysBooked: number;
    futureDaysBooked: number;
    bookingsReceived: number;
    cancelledBookings: number;
    cancelledDays: number;
};

type BookingPaceState = {
    bookingPaceStats: Map<string, BookingPaceStats>;
    bookingPaceStatsPrevious: Map<string, BookingPaceStats>;
    bookingPaceStatsPreviousYear: Map<string, BookingPaceStats>;
    startDate: Date;
    endDate: Date;
    previousStartDate: Date;
    previousEndDate: Date;
    previousYearStartDate: Date;
    previousYearEndDate: Date;
};

const INITIAL_DATE_RANGE_DAYS = 10;

const initializeDates = () => {
    const endDate = new Date();
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - INITIAL_DATE_RANGE_DAYS);

    const [previousStartDate, previousEndDate] = getPreviousPeriodDates(startDate, endDate);
    const [previousYearStartDate, previousYearEndDate] = getPreviousYearDates(startDate, endDate);

    return {
        startDate,
        endDate,
        previousStartDate,
        previousEndDate,
        previousYearStartDate,
        previousYearEndDate,
    };
};

const initialDates = initializeDates();

const initialState: BookingPaceState = {
    bookingPaceStats: new Map(),
    bookingPaceStatsPrevious: new Map(),
    bookingPaceStatsPreviousYear: new Map(),
    ...initialDates,
};

const emptyStats = (): BookingPaceStats => ({
    grossAdr: 0,
    netAdr: 0,
    grossIncome: 0,
    netIncome: 0,
    futureGrossIncome: 0,
    futureNetIncome: 0,
    currentGrossIncome: 0,
    currentNetIncome: 0,
    futureGrossAdr: 0,
    futureNetAdr: 0,
    currentGrossAdr: 0,
    currentNetAdr: 0,
    daysBooked: 0,
    currentDaysBooked: 0,
    futureDaysBooked: 0,
    bookingsReceived: 0,
    cancelledBookings: 0,
    cancelledDays: 0,
});

// A cancellation that retained guest compensation carries income with zero booked
// nights, and currentDaysBooked is a subtraction that can go negative on bad data.
// Only a positive night count is a valid denominator; anything else means "no ADR",
// never Infinity or a sign-flipped rate (the render site's `|| 0` misses both).
const adr = (income: number, nights: number): number => (nights > 0 ? income / nights : 0);

const bookingPaceSlice = createSlice({
    name: "bookingPace",
    initialState: initialState,
    reducers: {
        setStartDate: (state, action: PayloadAction<Date>) => {
            state.startDate = action.payload;
            [state.previousStartDate, state.previousEndDate] = getPreviousPeriodDates(state.startDate, state.endDate);
            [state.previousYearStartDate, state.previousYearEndDate] = getPreviousYearDates(state.startDate, state.endDate);
        },
        setEndDate: (state, action: PayloadAction<Date>) => {
            state.endDate = action.payload;
            [state.previousStartDate, state.previousEndDate] = getPreviousPeriodDates(state.startDate, state.endDate);
            [state.previousYearStartDate, state.previousYearEndDate] = getPreviousYearDates(state.startDate, state.endDate);
        },
        setPreviousStartDate: (state, action: PayloadAction<Date>) => {
            state.previousStartDate = action.payload;
        },
        setPreviousEndDate: (state, action: PayloadAction<Date>) => {
            state.previousEndDate = action.payload;
        },
        setPreviousYearStartDate: (state, action: PayloadAction<Date>) => {
            state.previousYearStartDate = action.payload;
        },
        setPreviousYearEndDate: (state, action: PayloadAction<Date>) => {
            state.previousYearEndDate = action.payload;
        },
        updateStats: (
            state,
            action: PayloadAction<{
                data: BookingPaceItem[];
                type: "current" | "previous" | "previousYear";
                apartmentId: number;
                groupId: number;
            }>,
        ) => {
            const { data, type, apartmentId, groupId } = action.payload;
            let records = data || [];
            if (apartmentId !== 0) {
                records = records.filter((a) => a.apartmentId === apartmentId);
            } else if (groupId !== 0) {
                records = records.filter((a) => a.groupId === groupId);
            }

            const stats = new Map<string, BookingPaceStats>();
            stats.set("All Sources", emptyStats());

            records.forEach((record) => {
                const key = getPlatformName(record.platformId);
                let currentStats = stats.get(key) || emptyStats();

                currentStats.grossIncome += record.grossIncome;
                currentStats.netIncome += record.netIncome;
                currentStats.daysBooked += record.daysBooked;
                currentStats.futureDaysBooked += record.futureDaysBooked;
                currentStats.currentDaysBooked += record.daysBooked - record.futureDaysBooked;
                currentStats.bookingsReceived += record.bookingsReceived;
                currentStats.futureGrossIncome += record.futureGrossIncome;
                currentStats.futureNetIncome += record.futureNetIncome;
                currentStats.currentGrossIncome += record.grossIncome - record.futureGrossIncome;
                currentStats.currentNetIncome += record.netIncome - record.futureNetIncome;
                currentStats.cancelledBookings += record.cancelledBookings;
                currentStats.cancelledDays += record.cancelledDays;

                stats.set(key, currentStats);

                let allSourcesStats = stats.get("All Sources")!;
                allSourcesStats.grossIncome += record.grossIncome;
                allSourcesStats.netIncome += record.netIncome;
                allSourcesStats.futureGrossIncome += record.futureGrossIncome;
                allSourcesStats.futureNetIncome += record.futureNetIncome;
                allSourcesStats.daysBooked += record.daysBooked;
                allSourcesStats.futureDaysBooked += record.futureDaysBooked;
                allSourcesStats.currentDaysBooked += record.daysBooked - record.futureDaysBooked;
                allSourcesStats.bookingsReceived += record.bookingsReceived;
                allSourcesStats.currentGrossIncome += record.grossIncome - record.futureGrossIncome;
                allSourcesStats.currentNetIncome += record.netIncome - record.futureNetIncome;
                allSourcesStats.cancelledBookings += record.cancelledBookings;
                allSourcesStats.cancelledDays += record.cancelledDays;
            });

            stats.forEach((value) => {
                value.grossAdr = adr(value.grossIncome, value.daysBooked);
                value.netAdr = adr(value.netIncome, value.daysBooked);
                value.futureGrossAdr = adr(value.futureGrossIncome, value.futureDaysBooked);
                value.futureNetAdr = adr(value.futureNetIncome, value.futureDaysBooked);
                value.currentGrossAdr = adr(value.currentGrossIncome, value.currentDaysBooked);
                value.currentNetAdr = adr(value.currentNetIncome, value.currentDaysBooked);
            });

            switch (type) {
                case "current":
                    state.bookingPaceStats = stats;
                    break;
                case "previous":
                    state.bookingPaceStatsPrevious = stats;
                    break;
                case "previousYear":
                    state.bookingPaceStatsPreviousYear = stats;
                    break;
            }
        },
    },
});

export const {
    setStartDate,
    setEndDate,
    setPreviousStartDate,
    setPreviousYearStartDate,
    setPreviousYearEndDate,
    setPreviousEndDate,
    updateStats,
} = bookingPaceSlice.actions;

export default bookingPaceSlice.reducer;
